import { useQuery } from '@tanstack/react-query';

import { fetchAdminNames } from 'app/hooks/useFetchReconciliationsPage';

import SalaryReconciliationsApiHandler from 'app/services/salaryReconciliationsApiHandler';

import { ListPage } from 'app/types/reconciliations';
import {
  SalaryReconciliationDiscrepanciesRequest,
  SalaryReconciliationDiscrepancy,
} from 'app/types/salaryReconciliations';

export const SALARY_DISCREPANCIES_QUERY_KEY = 'fetchSalaryDiscrepancies';

/**
 * Lists a salary accrual reconciliation run's discrepancies in one view: new, resolved, from earlier pay cycles, or
 * fixed by hand. Names the admins who fixed them by hand, and still lists them if the names can't be read.
 */
const useFetchSalaryDiscrepancies = (
  { id, view, companyId, page, size }: SalaryReconciliationDiscrepanciesRequest,
  enabled = true
) =>
  useQuery<ListPage<SalaryReconciliationDiscrepancy>, Error>(
    [SALARY_DISCREPANCIES_QUERY_KEY, id, view, companyId, page, size],
    async () => {
      const discrepancies = await SalaryReconciliationsApiHandler.listDiscrepancies({
        id,
        view,
        companyId,
        page,
        size,
      });

      const resolverIds = [
        ...new Set(
          discrepancies.list
            .map((discrepancy) => discrepancy.resolvedBy)
            .filter((resolvedBy): resolvedBy is string => Boolean(resolvedBy))
        ),
      ];
      const resolverNames = await fetchAdminNames(resolverIds);

      return {
        ...discrepancies,
        list: discrepancies.list.map((discrepancy) => ({
          ...discrepancy,
          resolvedByName: discrepancy.resolvedBy
            ? resolverNames.get(discrepancy.resolvedBy)
            : undefined,
        })),
      };
    },
    { enabled: enabled && !!id, keepPreviousData: true }
  );

export default useFetchSalaryDiscrepancies;
